import React, { useEffect, useRef } from 'react';
import styles from './styles/LoginRegister.module.css';

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

/**
 * Without a site key there's no widget to render, so forms shouldn't wait on a
 * token. The server skips verification outside production when it has no secret.
 */
export const TURNSTILE_ENABLED = Boolean(SITE_KEY);

/**
 * Renders a Cloudflare Turnstile check and reports its token.
 *
 * Tokens are single-use, so the parent changes `resetKey` after every submit
 * that sent one, and the widget fetches a fresh token.
 * @param {function} onToken - Called with the token, or null when it expires or errors.
 * @param {*} [resetKey] - Change this to reset the widget.
 */
function TurnstileWidget({ onToken, resetKey }) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const lastResetKeyRef = useRef(resetKey);

    // The widget keeps the callbacks it was rendered with; route them through a
    // ref so they always reach the latest onToken.
    const onTokenRef = useRef(onToken);
    onTokenRef.current = onToken;

    useEffect(() => {
        if (!TURNSTILE_ENABLED) return undefined;
        let intervalId = null;

        const renderWidget = () => {
            if (!window.turnstile || !containerRef.current) return false;

            widgetIdRef.current = window.turnstile.render(containerRef.current, {
                sitekey: SITE_KEY,
                callback: (token) => onTokenRef.current(token),
                'expired-callback': () => onTokenRef.current(null),
                'error-callback': () => onTokenRef.current(null),
            });
            return true;
        };

        // Try immediately; if the Turnstile script hasn't loaded yet, poll until it has.
        if (!renderWidget()) {
            intervalId = setInterval(() => {
                if (renderWidget()) {
                    clearInterval(intervalId);
                    intervalId = null;
                }
            }, 100);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
            if (widgetIdRef.current !== null && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (lastResetKeyRef.current === resetKey) return;
        lastResetKeyRef.current = resetKey;

        if (widgetIdRef.current !== null && window.turnstile) {
            onTokenRef.current(null);
            window.turnstile.reset(widgetIdRef.current);
        }
    }, [resetKey]);

    if (!TURNSTILE_ENABLED) return null;

    return <div ref={containerRef} className={styles.captchaContainer}></div>;
}

export default TurnstileWidget;
